/**
 * The header's per-layer counts, derived from the same map that marks the fields so the two can
 * never disagree about what is set.
 */

import { BeneathLayer, DifferingKeyDetail } from '../fields/FieldWrapper';
import { beneathLayers, OwnLayer } from './layerDetail';

export interface FieldRef {
  id: string;
  label: string;
}

export interface SourceChip {
  labelKey: string;
  color: string;
  items: FieldRef[];
}

const SITE_HUE = '#5b6fb3';
const OWNER_HUE = '#0f766e';
const DEVICE_HUE = '#7c3aed';
const STAGED_HUE = '#b3770c';

// One chip per layer beneath, so a count never lumps two layers together. Same keys the field
// flags use, so the header and the fields cannot name a layer differently.
const LAYER_CHIP_KEY: Record<BeneathLayer, string> = {
  owner: 'device-config.common.owner-setting',
  site: 'device-config.common.site-setting',
};
const LAYER_CHIP_HUE: Record<BeneathLayer, string> = { owner: OWNER_HUE, site: SITE_HUE };

export interface SourceChipsInput {
  /** Absent on the bulk form, which has no layer of its own to count. */
  layerDetail?: Map<string, DifferingKeyDetail[]>;
  siteDetail?: Map<string, DifferingKeyDetail[]>;
  ownLayer?: OwnLayer;
  /** Whether every layer beneath the form's own could be read. */
  layersResolved: boolean;
  fieldRefs: (ids: string[]) => FieldRef[];
}

/**
 * Fields resolving to the device type's default are deliberately not among the counts: they are the
 * majority, and their absence from every marker is what says so.
 */
export const buildSourceChips = ({
  layerDetail,
  siteDetail,
  ownLayer,
  layersResolved,
  fieldRefs,
}: SourceChipsInput): SourceChip[] => {
  if (!layerDetail) {
    // The bulk form, which can only say that the selection diverges from the site.
    return [
      {
        labelKey: 'device-config.site-overrides',
        color: SITE_HUE,
        items: fieldRefs(siteDetail ? [...siteDetail.keys()] : []),
      },
    ];
  }

  const idsWhere = (match: (source?: string) => boolean) =>
    [...layerDetail.entries()].filter(([, detail]) => match(detail[0]?.source)).map(([id]) => id);

  const idsFrom = (layer: BeneathLayer) =>
    [...layerDetail.entries()]
      .filter(([, detail]) => detail[0]?.source === 'inherited' && detail[0]?.inheritedFrom === layer)
      .map(([id]) => id);

  const own: SourceChip = {
    labelKey: ownLayer === 'device' ? 'device-config.device-overrides' : 'device-config.source.site-set',
    color: ownLayer === 'device' ? DEVICE_HUE : SITE_HUE,
    items: fieldRefs(idsWhere((source) => source === 'own')),
  };
  const staged = fieldRefs(idsWhere((source) => source === 'own-cleared' || source === 'own-will-set'));

  return [
    // Widest first, so the row reads in the order configuration resolves: site, then resident, then
    // this device. `beneathLayers` is narrowest first -- it answers "which layer does an edit here
    // displace" -- so it is reversed rather than read as given.
    //
    // A count that could not be read is worse than no count: "0 from site settings" reads as a site
    // that sets nothing. A form with no layer beneath its own has none of these to show.
    ...(layersResolved && ownLayer
      ? [...beneathLayers(ownLayer)].reverse().map((layer) => ({
          labelKey: LAYER_CHIP_KEY[layer],
          color: LAYER_CHIP_HUE[layer],
          items: fieldRefs(idsFrom(layer)),
        }))
      : []),
    own,
    // Only once something is staged: a standing "0 not saved yet" is noise, where the count of what
    // the layer holds is worth stating even at zero.
    ...(staged.length ? [{ labelKey: 'device-config.staged-fields', color: STAGED_HUE, items: staged }] : []),
  ];
};
